import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import MainTitle from '../../../components/ui/MainTitle';
import SubTitle from '../../../components/ui/SubTitle';
import ImageUploader from '../../../components/store/products/ImageUploader';
import ProductForm from '../../../components/store/products/ProductForm';
import SubmitButton from '../../../components/form/SubmitButton';
import ButtonSelect from '../../../components/form/ButtonSelect';
import { useProductCategories } from '../../../hooks/store/productCategory';
import { useUpdateResource } from '../../../hooks/useUpdateResource';
import { useNavigate, useNavigation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoadingButton from '../../../components/form/LoadingButton';
import { apiPost } from '../../../utils/api';
import { getAdminAccessToken, getStoreAccessToken } from '../../../utils/auth';
import { showError, showSuccess } from '../../../utils/alert';
import { formatNumber } from '../../../utils/formatNumber';

type FormValues = {
  productNameEn: string;
  productNameAr: string;
  shortDescriptionEn: string;
  shortDescriptionAr: string;
  longDescriptionEn: string;
  longDescriptionAr: string;
  price: number;
};

export default function AddProduct() {
   const { storeId} = useParams<{ storeId: string}>();
  const numericStoreId = storeId ? parseInt(storeId) : undefined;

  const [images, setImages] = useState<File[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedPrepTime, setSelectedPrepTime] = useState<number | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>();

  const{data:categories} = useProductCategories(numericStoreId);

    const {t} = useTranslation();
    const navigate = useNavigate();
    const [loading,setLoading] = useState(false);

    const prepTimes = [
    { id: 5, label: `${formatNumber(5)}  ${t('mins')}` },
    { id: 10, label: `${formatNumber(10)}  ${t('mins')}` },
    { id: 15, label: `${formatNumber(15)}  ${t('mins')}` },
    { id: 20, label:  `${formatNumber(20)}  ${t('mins')}` },
    { id: 25, label:  `${formatNumber(25)}  ${t('mins')}` },
    { id: 30, label:  `${formatNumber(30)}  ${t('mins')}` },
  ];

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
  if (selectedCategory === null || selectedPrepTime === null) {
    alert("Please select category and preparation time");
    return;
  }
  setLoading(true);
  const languages = [
    {
      languageCode: "en",
      name: data.productNameEn,
      shortDescription: data.shortDescriptionEn,
      longDescription: data.longDescriptionEn,
    },
    {
      languageCode: "ar",
      name: data.productNameAr,
      shortDescription: data.shortDescriptionAr,
      longDescription: data.longDescriptionAr,
    },
  ];
  const formData = new FormData();
  formData.append("preparationTime", String(selectedPrepTime));
  formData.append("basePrice", String(data.price));
  formData.append("categoryId", String(selectedCategory));
  formData.append("languages", JSON.stringify(languages));
  images.forEach((file, index) => {
    formData.append("images", file);
  });
    try{
      const endPoint = numericStoreId ? `product/create?storeId=${numericStoreId}`:"product/create";
      const response = await apiPost(endPoint, formData,
        {Authorization:numericStoreId? getAdminAccessToken():getStoreAccessToken()}) as { message: string ,productId:number};
      showSuccess(t("product_created_success"));
      setLoading(false);
      const navigateLink  = numericStoreId 
      ?
      `/admin/stores/${numericStoreId}/products/${response.productId}/extra-items` 
      :
      `/store/products/${response.productId}/extra-items`;
      
      navigate(navigateLink);
    }
    catch(err:any){
      setLoading(false);
      showError(err.message);
    }
};


  return (
    <div className="container p-6 h-[100vh] overflow-auto">
      <div className="mb-14">
        <MainTitle title="add_new_product"/>
      </div>

      <div className="mb-14">
        <SubTitle title="product_images" hint="max_5_images"/>
        <ImageUploader label='add_photo' images={images} setImages={setImages} maxImages={5} />
      </div>

      <div className="mb-6">
        <SubTitle title="product_details"/>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-[800px]">
          <ProductForm register={register} errors={errors} />

            <div>
            <SubTitle title='select_category'/>
            <ButtonSelect
            options={categories?categories.map(c=>({id:c.id,label:c.languages[0].title})):[]}
            selected={selectedCategory}
            onSelect={setSelectedCategory}
            className="mb-4"
          />
          </div>

          <div>
            <SubTitle title='select_preparation_time'/>
            <ButtonSelect
            options={prepTimes}
            selected={selectedPrepTime}
            onSelect={setSelectedPrepTime}
            className="mb-4"
          />
          </div>
          {
            loading
            ?
            <LoadingButton/>
            :
            <SubmitButton label='create'/>
          }
        </form>
      </div>
    </div>
  );
}
